import {
  Box,
  Button,
  Callout,
  Flex,
  Spinner,
  Text,
  Tooltip,
} from "@radix-ui/themes";

import {
  DataTable,
  type DataTableColumns,
} from "~/v3-poc/components/DataTable";
import type { BalanceRow } from "./balances.utils";

export interface BalancesWidgetPresentationProps {
  rows: BalanceRow[];
  previousCloseAsOf?: string;
  isLoading: boolean;
  isFetching: boolean;
  isUnavailable: boolean;
  errorMessage?: string;
  onRetry: () => void;
  isAccountReady: boolean;
}

const COLUMNS: DataTableColumns<BalanceRow> = [
  {
    id: "section",
    header: "Category",
    accessorKey: "section",
    cell: ({ row }) => (
      <Text size="1" color="gray">
        {row.original.section}
      </Text>
    ),
  },
  {
    id: "balance",
    header: "Balance",
    accessorKey: "label",
    cell: ({ row }) => (
      <Tooltip content={row.original.tooltip}>
        <Text size="1" weight="medium" as="span">
          {row.original.label}
        </Text>
      </Tooltip>
    ),
  },
  {
    id: "previousClose",
    header: "Previous close",
    accessorKey: "previousCloseLabel",
    meta: { align: "end", numeric: true },
    cell: ({ row }) => <Text size="1">{row.original.previousCloseLabel}</Text>,
  },
  {
    id: "current",
    header: "Current",
    accessorKey: "currentLabel",
    meta: { align: "end", numeric: true },
    cell: ({ row }) => <Text size="1">{row.original.currentLabel}</Text>,
  },
];

const BalancesError = ({
  errorMessage,
  isFetching,
  onRetry,
}: {
  errorMessage?: string;
  isFetching: boolean;
  onRetry: () => void;
}) => (
  <Callout.Root color="red" role="alert">
    <Callout.Text>
      {errorMessage ?? "Balances could not be loaded."}
    </Callout.Text>
    <Button
      type="button"
      size="1"
      variant="soft"
      color="red"
      onClick={onRetry}
      loading={isFetching}
    >
      Retry
    </Button>
  </Callout.Root>
);

export const BalancesWidgetPresentation = ({
  rows,
  previousCloseAsOf,
  isLoading,
  isFetching,
  isUnavailable,
  errorMessage,
  onRetry,
  isAccountReady,
}: BalancesWidgetPresentationProps) => {
  const emptyMessage = isAccountReady
    ? "No balance data to show."
    : "Select an account to view balances.";

  return (
    <Flex direction="column" height="100%" minHeight="0">
      <Flex
        align="center"
        justify="between"
        gap="2"
        px="2"
        py="1"
        flexShrink="0"
        wrap="wrap"
        className="border-[color:var(--gray-a5)] border-b"
      >
        <Text size="1" color="gray">
          Account balances
        </Text>
        <Text size="1" color="gray" className="atd-numeric">
          {previousCloseAsOf
            ? `Previous close · ${previousCloseAsOf}`
            : "Previous close"}
        </Text>
      </Flex>

      {isUnavailable && rows.length > 0 ? (
        <Box p="2" flexShrink="0">
          <BalancesError
            errorMessage={errorMessage}
            isFetching={isFetching}
            onRetry={onRetry}
          />
        </Box>
      ) : null}

      <Box flexGrow="1" minHeight="0" overflow="auto" position="relative">
        {isLoading && rows.length === 0 ? (
          <Flex align="center" justify="center" height="100%" p="4">
            <Spinner size="2" />
          </Flex>
        ) : isUnavailable && rows.length === 0 ? (
          <Box p="2">
            <BalancesError
              errorMessage={errorMessage}
              isFetching={isFetching}
              onRetry={onRetry}
            />
          </Box>
        ) : (
          <DataTable
            columns={COLUMNS}
            data={rows}
            variant="ghost"
            size="1"
            caption="Current and previous account balances"
            emptyMessage={emptyMessage}
          />
        )}
      </Box>
    </Flex>
  );
};
